/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useEffect, useState } from "react";
import { getPlanForAffiliate } from "../core/_requests";
import { useNavigate, useLocation } from "react-router-dom";
import { Plan, PlanDetail } from "../core/_models";
import SocialMediaIcons from "./SocialMedia";

const SalesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscribedActivePlan, setSubscribedActivePlan] = useState<Plan | null | any>(null);
  const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null);
  const [selectedPlanOption, setSelectedPlanOption] = useState<{ [key: string]: PlanDetail | null | any }>({});
  const [selectedPlanAmount, setSelectedPlanAmount] = useState("");


  // Extract planId from the URL query parameters
  const getPlanIdFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('planId');
  };

  const fetchPlans = async () => {
    try {
      const res = await getPlanForAffiliate();
      const plansData: Plan[] = res?.data || []; // Use the plansData directly

      setPlans(plansData); // No filtering applied

      // Set the selected plan from URL (if any)
      const planIdFromURL = getPlanIdFromURL();
      if (planIdFromURL) {
        const activePlan = plansData.find(plan => plan.id === planIdFromURL); // Use plansData instead of filteredPlans
        if (activePlan) {
          setSubscribingPlan(planIdFromURL);
          setSubscribedActivePlan(activePlan);
        }
      }

      const initialSelectedOptions: { [key: string]: PlanDetail | null } = {};
      setSelectedPlanOption(initialSelectedOptions);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchPlans();
  }, []);

  const handlePlanOptionChange = (planId: any, amount: any, validity: any, validityCount: any, type: any, isActive: any) => {
    setSelectedPlanOption((prevState) => {
      const newState = { ...prevState };

      Object.keys(newState).forEach((key) => {
        if (key !== planId && newState[key]?.isActive === false) {
          newState[key] = null;
        }
      });

      if (type !== 2) {
        let offerAmount = amount;
        newState[planId] = { amount, validityCount, validity, type, isActive };
        setSelectedPlanAmount(offerAmount);
      }

      return newState;
    });
  };

  useEffect(() => {
    if (subscribingPlan) {
      const trackingPixel = document.createElement("script");
      trackingPixel.type = "text/javascript";
      trackingPixel.src = `https://www.jvzoo.com/tracking/pixel_code/${subscribingPlan}`;
      document.body.appendChild(trackingPixel);
      // Save the planId to localStorage when the JVZoo pixel is added
      localStorage.setItem('jvzooPlanId', subscribingPlan);
      return () => {
        document.body.removeChild(trackingPixel);
      };
    }
  }, [subscribingPlan]);


  // Retrieve the JVZoo plan ID from localStorage (for use later)
  const storedPlanId = localStorage.getItem('jvzooPlanId');
  
  const [isLeftBarActive, setLeftBarActive] = useState(false);


  const handleMenuItemClick = () => {
    // Remove the active classes
    setLeftBarActive(false);
    document.body.classList.remove("overflow-hidden");
  };
  return (
    <section className="container-fluid py-3">
      <section className="head row top-header-sales-page">
        <div className="col-12 col-md-2 d-flex align-items-center justify-content-start">
          <div className="nav-brand right-form ">
            <img
              className="img-fluid mx-auto"
              src="/assets/images/logo.svg"
              alt="Logo"
              style={{ height: "60px" }}
            />
          </div>
        </div>

        <div className="col-12 col-md-6 d-flex">
          {/* For large screens */}
          <div className="col-12 text-center">
            <h1 className="fs-3 fw-bold text-success">
              Welcome to Frayo – Empowering Chefs Everywhere!
            </h1>


          </div>
        </div>

        <div className="col-12 col-md-4 d-flex ss-ico">
          <SocialMediaIcons />
        </div>
      </section>

      <div className="plan-container justify-content-center mb-2">
        <div className="col-lg-8 col-md-8 col-sm-12 offset-lg-2 offset-md-2 text-center mx-3">
          <p className="text-muted">
            Frayo is a dynamic platform designed to connect talented chefs with food lovers.
            Whether you're just starting out or looking to scale your culinary business,
            Frayo provides all the tools and support you need to succeed in the competitive food industry.
          </p>
        </div>



        {loading ? (
          <div className="col-12 text-center">Loading...</div>
        ) : plans?.length > 0 ? (
          plans
            ?.filter((plan: any) => subscribedActivePlan?.id === plan.id) // Filter active plans
            .map((plan: any) => (
              <div key={plan?.id} className="col-lg-4 col-md-6 col-sm-12">
                <div className="plan-box-sales-page card p-4 mb-4">
                  <div className="plan-header d-flex flex-column align-items-center">
                    {plan.icon ? (
                      <img
                        className="img-fluid mb-2"
                        src={plan?.icon}
                        alt="Frayo"
                        height="90px"
                        width="90px"
                      />
                    ) : null}
                    <h5 className="plan-type fs-12 text-success mb-0 fw-bold text-center mb-2">
                      {plan.name}
                    </h5>
                    {plan.description && (
                      <p className="fs-12 text-center mt-2" style={{ maxWidth: "500px" }}>
                        {/* Subscription Plan {plan.description} */}
                        A subscription plan for Frayo Chefs expanding their menu and customer base,
                        aiming to elevate their offerings. </p>
                    )}
                  </div>

                  {plan.isPlanActive && plan.plantype.planTypeName === "!Free Plan" && (
                    <div className="subscription-options mb-3">
                      <label
                        className="form-check-label fw-bold mt-2 badge bg-danger ms-9"
                        style={{
                          backgroundColor: "#f8d7da",
                          padding: "12px",
                          borderRadius: "12px",
                        }}
                      >
                        {plan?.plan_start_date && (
                          <span>
                            Validity:{" "}
                            {new Date(plan.plan_start_date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}{" "}
                            to{" "}
                            {new Date(plan.plan_end_date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        )}
                      </label>
                    </div>
                  )}

                  {plan.plan_details.map((detail: PlanDetail) => (
                    <div key={detail.validity} className="form-check">
                      {detail.type == 1 ? (
                        <>
                          <input
                            type="radio"
                            id={`plan-${plan.id}-${detail.validity}`}
                            name={`plan-${plan.id}`}
                            value={detail.type}
                            className="form-check-input"
                            onChange={() => {
                              handlePlanOptionChange(
                                plan.id,
                                detail.amount,
                                detail.validity,
                                detail.validityCount,
                                detail.type,
                                detail.isActive
                              );
                            }}
                            checked={plan.id === subscribingPlan || selectedPlanOption[plan.id]?.type === detail.type}
                            disabled={plan.isPlanActive === true && detail.isActive === true}
                          />
                          <label
                            className={`form-check-label ${detail.type === 1 && detail.isActive ? "text-danger" : "text-success fw-bold"}`}
                            htmlFor={`plan-${plan.id}-${detail.validity}`}
                          >
                            ${detail.amount} for {detail.validity}
                          </label>
                        </>
                      ) : (
                        <span></span>
                      )}
                    </div>
                  ))}

                  <div className="plan-details mt-3 mb-2">
                    <strong className="text-danger">Features:</strong>
                    <ul className="list-unstyled">
                      {plan.featuresDetails.map((feature: any) => (
                        <li key={feature.id} className="d-flex align-items-center my-2">
                          <img
                            src="/assets/images/vector.png"
                            alt="tick"
                            className="me-2"
                            style={{ width: "15px", height: "15px" }}
                          />
                          <span className="me-3" style={{ fontSize: "14px" }}>
                            {feature.name}&nbsp;
                            {feature.description && (
                              <i
                                className="material-symbols-outlined info-ico me-3"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={feature?.description}
                                style={{
                                  cursor: "pointer",
                                  position: "relative",
                                  top: "4px",
                                  fontSize: "16px",
                                  width: "20px",
                                  height: "20px",
                                }}
                              >
                                info
                              </i>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {subscribedActivePlan?.id === plan.id && (
                    <div className="col-lg-12 plan-footer d-flex justify-content-center align-items-center mt-3 mb-2">
                      {plan.id === "4f675ece-99bd-418f-8625-40228d489c07" && (
                        <a href="https://www.jvzoo.com/b/0/412945/99" className="btn btn-primary rounded-5 px-4 py-2">
                          Buy Now
                          <img
                            src="https://i.jvzoo.com/0/412945/99"
                            alt="Tracking Pixel"
                            style={{ border: '0', width: '1px', height: '1px' }}
                          />
                        </a>
                      )}

                      {plan.id === "9543c79a-48d0-4013-8082-14d5384c6d82" && (
                        <a href="https://www.jvzoo.com/b/0/412947/99" className="btn btn-primary rounded-5 px-4 py-2">
                          Buy Now
                          <img
                            src="https://i.jvzoo.com/0/412947/99"
                            alt="Tracking Pixel"
                            style={{ border: '0', width: '1px', height: '1px' }}
                          />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
        ) : (
          <div className="col-12 text-center">No plans available.</div>
        )}
      </div>
    </section>

  );
};

export default SalesPage;
